/**
 * Hero carousel (Layout B). Vanilla JS, no dependency. Handles slide
 * switching, arrow/dot controls, swipe, keyboard arrows when focused,
 * and optional autoplay (paused on interaction, disabled entirely under
 * prefers-reduced-motion).
 */

if (!customElements.get('hero-carousel')) {
  class HeroCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-hero-carousel-track]');
      this.slides = Array.from(this.querySelectorAll('[data-hero-carousel-slide]'));
      if (this.slides.length < 2) return;

      this.prevButton = this.querySelector('[data-hero-carousel-prev]');
      this.nextButton = this.querySelector('[data-hero-carousel-next]');
      this.dots = Array.from(this.querySelectorAll('[data-hero-carousel-dot]'));
      this.currentLabel = this.querySelector('[data-hero-carousel-current]');

      this.index = 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reducedMotion;
      this.autoplayInterval = (Number(this.dataset.autoplayInterval) || 5) * 1000;
      this.autoplayTimer = null;

      this.prevButton?.addEventListener('click', () => {
        this.goTo(this.index - 1);
        this.pauseAutoplay();
      });
      this.nextButton?.addEventListener('click', () => {
        this.goTo(this.index + 1);
        this.pauseAutoplay();
      });
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          this.goTo(i);
          this.pauseAutoplay();
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          this.goTo(this.index - 1);
          this.pauseAutoplay();
        } else if (event.key === 'ArrowRight') {
          this.goTo(this.index + 1);
          this.pauseAutoplay();
        }
      });

      this.setupSwipe();
      this.addEventListener('pointerenter', () => this.pauseAutoplay());
      this.addEventListener('focusin', () => this.pauseAutoplay());

      if (this.autoplayEnabled) this.startAutoplay();
    }

    goTo(nextIndex) {
      const total = this.slides.length;
      const normalized = ((nextIndex % total) + total) % total;
      if (normalized === this.index) return;

      this.slides[this.index].hidden = true;
      this.slides[this.index].classList.remove('hero-carousel__slide--active');
      this.slides[normalized].hidden = false;
      this.slides[normalized].classList.add('hero-carousel__slide--active');

      this.dots[this.index]?.classList.remove('hero-carousel__dot--active');
      this.dots[this.index]?.setAttribute('aria-selected', 'false');
      this.dots[normalized]?.classList.add('hero-carousel__dot--active');
      this.dots[normalized]?.setAttribute('aria-selected', 'true');

      if (this.currentLabel) {
        this.currentLabel.textContent = String(normalized + 1).padStart(2, '0');
      }

      this.index = normalized;
    }

    startAutoplay() {
      this.autoplayTimer = setInterval(() => this.goTo(this.index + 1), this.autoplayInterval);
    }

    pauseAutoplay() {
      if (!this.autoplayTimer) return;
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    setupSwipe() {
      let startX = 0;
      let startY = 0;
      let tracking = false;

      this.track.addEventListener(
        'pointerdown',
        (event) => {
          startX = event.clientX;
          startY = event.clientY;
          tracking = true;
        },
        { passive: true }
      );

      this.track.addEventListener(
        'pointerup',
        (event) => {
          if (!tracking) return;
          tracking = false;
          const deltaX = event.clientX - startX;
          const deltaY = event.clientY - startY;
          if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
          this.pauseAutoplay();
          if (deltaX < 0) {
            this.goTo(this.index + 1);
          } else {
            this.goTo(this.index - 1);
          }
        },
        { passive: true }
      );
    }
  }

  customElements.define('hero-carousel', HeroCarousel);
}
